/**
 * DurationRangeSlider - Two-thumb range slider for slider item duration
 *
 * Pattern adapted from VariantRangeSlider in A/B test editor.
 * Each item has range [start, end] where:
 * - First item always starts at 0
 * - Last item always ends at 100
 * - Adjacent items share boundaries (end of N = start of N+1)
 *
 * Key behavior: Moving a boundary only affects the TWO adjacent items,
 * not all items (unlike proportional redistribution).
 *
 * Used in SliderItemEditor for per-item duration control.
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import { Slider, Box, Typography } from '@mui/material';
import { MIN_DURATION_PERCENT } from '../../../../utils/durationDistribution';

export interface DurationRangeSliderProps {
  /** Range [start, end] from calculateRanges() */
  value: [number, number];

  /** Callback when range changes - passed to handleRangeChange in parent */
  onRangeChange: (newRange: [number, number]) => void;

  /** Total duration in seconds for calculating actual time */
  totalDurationSeconds: number;

  /** Whether this is the first item (left thumb fixed at 0) */
  isFirst: boolean;

  /** Whether this is the last item (right thumb fixed at 100) */
  isLast: boolean;

  /** Whether slider is disabled */
  disabled?: boolean;

  /** Test ID for the slider */
  testId?: string;
}

/**
 * Calculate weight (percentage) from range
 */
function calculateWeight(range: [number, number]): number {
  return range[1] - range[0];
}

/**
 * Calculate actual duration in seconds from range and total duration
 */
function calculateActualSeconds(range: [number, number], totalSeconds: number): number {
  const weight = calculateWeight(range);
  return (totalSeconds * weight) / 100;
}

/**
 * Format seconds for display (1 decimal place)
 */
function formatSeconds(seconds: number): string {
  return seconds.toFixed(1);
}

/**
 * DurationRangeSlider - Two-thumb MUI Slider
 *
 * Features:
 * - Local state for smooth slider interaction during drag
 * - Commits to parent only on release (onChangeCommitted)
 * - Shows range, percentage and actual time: "Range: 0 - 50 (50%) • 7.5s"
 * - First item: left thumb disabled (always 0)
 * - Last item: right thumb disabled (always 100)
 * - disableSwap prevents thumbs from crossing
 */
export const DurationRangeSlider: React.FC<DurationRangeSliderProps> = memo(
  function DurationRangeSlider({
    value,
    onRangeChange,
    totalDurationSeconds,
    isFirst,
    isLast,
    disabled = false,
    testId = 'duration-range-slider',
  }) {
    // Local state for smooth slider interaction
    const [localValue, setLocalValue] = useState<[number, number]>(value);

    // Sync with parent when value prop changes (e.g., after adjacent item change)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    // Handle slider drag - only update local state
    const handleChange = useCallback(
      (_event: Event, newValue: number | number[]) => {
        const range = newValue as [number, number];

        // Enforce constraints for first/last items
        let constrainedRange: [number, number] = [...range] as [number, number];

        if (isFirst) {
          // First item: left thumb must stay at 0
          constrainedRange[0] = 0;
        }

        if (isLast) {
          // Last item: right thumb must stay at 100
          constrainedRange[1] = 100;
        }

        // Ensure minimum weight (MIN_DURATION_PERCENT)
        const weight = constrainedRange[1] - constrainedRange[0];
        if (weight < MIN_DURATION_PERCENT) {
          // Don't allow the change
          return;
        }

        setLocalValue(constrainedRange);
      },
      [isFirst, isLast]
    );

    // Handle slider release - commit to parent
    const handleChangeCommitted = useCallback(
      (_event: React.SyntheticEvent | Event, newValue: number | number[]) => {
        const range = newValue as [number, number];

        // Apply same constraints
        let constrainedRange: [number, number] = [...range] as [number, number];

        if (isFirst) {
          constrainedRange[0] = 0;
        }

        if (isLast) {
          constrainedRange[1] = 100;
        }

        // Ensure minimum weight
        const weight = constrainedRange[1] - constrainedRange[0];
        if (weight < MIN_DURATION_PERCENT) {
          // Revert to previous value
          setLocalValue(value);
          return;
        }

        // Only notify parent if value changed
        if (constrainedRange[0] !== value[0] || constrainedRange[1] !== value[1]) {
          onRangeChange(constrainedRange);
        }
      },
      [isFirst, isLast, value, onRangeChange]
    );

    // Calculate display values
    const weight = calculateWeight(localValue);
    const actualSeconds = calculateActualSeconds(localValue, totalDurationSeconds);

    return (
      <Box
        data-testid={testId}
        sx={{
          width: '100%',
          mt: 1,
        }}
      >
        {/* Range info label */}
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            mb: 0.5,
          }}
        >
          Range: {localValue[0]} - {localValue[1]} ({weight}%) • {formatSeconds(actualSeconds)}s
        </Typography>

        {/* Two-thumb range slider */}
        <Slider
          getAriaLabel={(index) => (index === 0 ? 'Duration range start' : 'Duration range end')}
          value={localValue}
          onChange={handleChange}
          onChangeCommitted={handleChangeCommitted}
          min={0}
          max={100}
          disabled={disabled}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
          disableSwap
          sx={{
            '& .MuiSlider-thumb': {
              width: 16,
              height: 16,
              // Visually indicate disabled thumbs
              '&[data-index="0"]': isFirst
                ? {
                    backgroundColor: 'action.disabled',
                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }
                : {},
              '&[data-index="1"]': isLast
                ? {
                    backgroundColor: 'action.disabled',
                    '&:hover': {
                      boxShadow: 'none',
                    },
                  }
                : {},
            },
            '& .MuiSlider-valueLabel': {
              fontSize: 12,
            },
          }}
        />
      </Box>
    );
  }
);

export default DurationRangeSlider;
